import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  useTheme,
  Box
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  LightMode as LightIcon,
  DarkMode as DarkIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext'
import { useThemeColors } from '../../hooks/useThemeColors'

const navigationItems = [
  { path: '/', label: 'Gauge', icon: <DashboardIcon /> },
  { path: '/bar-chart', label: 'Bar Chart', icon: <BarChartIcon /> },
  { path: '/pie-chart', label: 'Pie Chart', icon: <PieChartIcon /> }
]

interface TopNavigationProps {
  onRefresh?: () => void
  refreshing?: boolean
}

const TopNavigation: React.FC<TopNavigationProps> = ({
  onRefresh,
  refreshing = false
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { mode, toggleTheme } = useCustomTheme()
  const colors = useThemeColors()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  return (
    <AppBar
      position='static'
      elevation={2}
      sx={{
        backgroundColor: colors.background.paper,
        color: colors.text.primary,
        borderBottom: `1px solid ${colors.divider}`
      }}>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        {/* Navigation Items */}
        <Box sx={{ display: 'flex', flexGrow: 1, gap: { xs: 0.5, sm: 1 } }}>
          {navigationItems.map((item) => (
            <IconButton
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              color={location.pathname === item.path ? 'primary' : 'default'}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 2,
                backgroundColor:
                  location.pathname === item.path
                    ? colors.primary + '20'
                    : 'transparent',
                '&:hover': {
                  backgroundColor:
                    location.pathname === item.path
                      ? colors.primary + '30'
                      : colors.background.default + '20'
                }
              }}>
              {item.icon}
            </IconButton>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
          {/* Refresh Button */}
          {onRefresh && (
            <IconButton
              onClick={onRefresh}
              disabled={refreshing}
              size={isMobile ? 'small' : 'medium'}
              color='primary'
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: colors.primary + '20'
                }
              }}>
              <RefreshIcon />
            </IconButton>
          )}

          {/* Theme Toggle */}
          <IconButton
            onClick={toggleTheme}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderRadius: 2,
              color: colors.text.secondary,
              '&:hover': {
                backgroundColor: colors.background.default + '20'
              }
            }}>
            {mode === 'light' ? <DarkIcon /> : <LightIcon />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default TopNavigation
